import { useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import BiztantuLoader from "../../Components/Loader";
import LeadAccordion from "../../Components/LeadAccordion";
import { useDispatch, useSelector } from "react-redux";
import { Stack } from "@mui/system";
import { handleValidateCodevalue } from "../../Utility/constant";
import InfoIcon from "../../Components/Widgets/InfoIcon";
import TruncatedTextCell from "../../Components/TruncatedTextCell";
import TextFiledWithLabel from "../../Components/TextFieldWithLabel";
import DataGridTable from "../../Components/DataGrid";
import { StyledGridContainer, StyledSubHeader } from "../../Components/Styled";
import { useSnackbar } from "notistack";
import { requestGetLeadSuccessPredictionData } from "../../Store/Lead/action";
import './Lead.css';

function ViewLeadSuccessScore({ leadId }) {
  const isLoadingProgress = useSelector((state) => state.global.isLoading);

    const internationalise = useSelector(state => state?.internationalise?.currentHashMapData);
    const leadScorePredictionData = useSelector(state => state?.lead?.leadScorePredictionData) || {}
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
      if (leadId) {
        dispatch(requestGetLeadSuccessPredictionData(leadId));
      }
    }, [dispatch, leadId]);

  const {
    "lead score (100)": leadScore,
    "engagement and response (35)": engagementScore,
    "sentiment and sales feedback (25)": sentimentSectionScore,
    "historical signals (25)": historicalScore,
    "lead source and context (15)": sourceContextScore,
    "sentiment score": sentimentScore,
    "customer score": customerScore,
    "lead probablity": leadProbability,
    "lead conversation": conversations = [],
    customer_info: customerInfo = {},
    followups,
    source,
    "existing customer": existingCustomer,
  } = leadScorePredictionData;

  const columns = useMemo(
    () => [
      {
        field: "note",
        headerName: "Note",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <TruncatedTextCell
            text={row.note}
            dialogTitle="LEAD_EDIT_FU_NOTE"
            stripHtml
            activityType={row.activityType}
            recipients={row.recipients}
            createdAt={row.createdAt}
            callStatus={row.callStatus}
            callDuration={row.callDuration}
          />
        ),
      },
    ],
    []
  );

  const customerFields = useMemo(
    () => [
      {
        label: "Existing Customer",
        value: customerInfo.is_existing_customer ? "Yes" : "No",
      },
      {
        label: "Leads Closed",
        value: customerInfo.leads_closed ?? 0,
      },
      {
        label: "Total Revenue",
        value: `₹${customerInfo.total_revenue ?? 0}`,
      },
      {
        label: "Closed in Last 6 Months",
        value: customerInfo.closed_in_last_6_months ?? 0,
      },
      {
        label: "Closure Ratio",
        value:
          customerInfo.ratio_closed_total != null
            ? customerInfo.ratio_closed_total.toFixed(2)
            : "0.00",
      },
    ],
    [customerInfo]
  );

  const renderValuesOnCard = [
    { field: "note",stripHtml:true,truncate : true},
];

  if (!leadScorePredictionData || Object.keys(leadScorePredictionData).length === 0) {
    return (
      <Box p={2}>
        <Typography>{handleValidateCodevalue(internationalise,"COMMON_NO_DATA_FOUND")}</Typography>
      </Box>
    );
  }

  return (
    <BiztantuLoader isChildren open={isLoadingProgress}>
        <Box display="flex" flexDirection="column" gap={2} height= "60vh" className = "hide-scrollbar">
            <Stack
                direction="row"
                spacing={3}
                alignItems="center"
                className="lead-score-banner"
              >
                <Typography variant="body2">
                  <strong>Lead Score</strong>: {leadScore ?? "-"} / 100
                </Typography>
              </Stack>
          <LeadAccordion
            label={`Engagement & Response (${engagementScore} / 35)`}
          >
          <InfoIcon
          text = "Measures how actively your team has engaged with the lead, based on the number of recorded follow up interactions."
          />
          <TextFiledWithLabel
            label = "Follow Ups"
            value = {followups}
            disabled
          />
          </LeadAccordion>

          {/* Sentiment */}
          <LeadAccordion
            label={`Sentiment & Sales Feedback (${sentimentSectionScore} / 25)`}
          >
          <InfoIcon
          text = "Measures the tone of the conversation between your team and the lead (positive or negative), derived from the sentiment analysis of the follow up notes using an AI language model."
          />
          
          <TextFiledWithLabel
            label = "Sentiment Score"
            value = {sentimentScore?.toFixed(2) ?? "-"}
            disabled
          />

                <StyledSubHeader 
                className = "mb-10"
                >
                  Lead Conversations
                </StyledSubHeader>

              <DataGridTable
                dataGridData={conversations}
                columns={columns}
                showId = {false}
                isRequiredToolBar={false}
                isCheckboxReq={false}
                renderValuesOnCard = {renderValuesOnCard}
                height = {{
                  xs: 'calc(80dvh - 350px)',
                  sm: "calc(40dvh - 140px)",
                  md: "calc(40dvh - 180px)",
                  lg: 'calc(100vh - 400px)',
                  xl: 'calc(100vh - 400px)'
                }}
              />
          </LeadAccordion>

          {/* Historical */}
       <LeadAccordion label={`Historical Signals (${historicalScore} / 25)`}>
       
          <InfoIcon
          text = "Historical Signals: Measures the lead success probability using ML models trained on historical data, such as patterns across customer demographics, income group, follow up count, lead value, and customer status."
          />
          <TextFiledWithLabel
            label="Lead Probability"
            value={`${leadProbability?.toFixed(2) ?? 0}%`}
            disabled
          />

            <StyledSubHeader>
              {`Customer Score (${customerScore?.toFixed(2) ?? 0})`}
            </StyledSubHeader>
            <StyledGridContainer className="lead-score-grid">
              {customerFields.map(({ label, value }) => (
                <TextFiledWithLabel
                  key={label}
                  label={label}
                  value={value}
                  disabled
                />
              ))}
            </StyledGridContainer>
            </LeadAccordion>

          {/* Lead Source */}
          <LeadAccordion
            label={`Lead Source & Context (${sourceContextScore} / 15)`}
          >
            
          <InfoIcon
          text = "Measures the quality of the lead's source, and whether they are an existing customer or not."
          />
          
            <StyledGridContainer className="lead-score-grid">
              <TextFiledWithLabel
                label="Source"
                value={source || "-"}
                disabled
              />

              <TextFiledWithLabel
                label="Existing Customer"
                value=
              {existingCustomer ? "Yes" : "No"}
                disabled
              />
              
            </StyledGridContainer>
          </LeadAccordion>
        </Box>
    </BiztantuLoader>
  );
}

export default ViewLeadSuccessScore;
